/**
 * Tour steps — Dashboard domain
 * Cubre: welcome, dashboard, posture-score, threat-map, risk-evolution, complete
 */

import {
  Shield,
  Sparkle as Sparkles,
  CheckCircle,
  Gauge,
  MapTrifold,
  ChartLineUp
} from '@phosphor-icons/react';

// ============ WELCOME (clave en quick) ============
const welcomeProspect = {
  id: 'welcome',
  title: 'Bienvenido a VEKTOR',
  description: 'Vamos a verlo en 90 segundos: cómo medir tu exposición al riesgo, detectar ataques antes de que pasen y cumplir compliance sin esfuerzo extra.',
  icon: Sparkles,
  position: 'center',
  highlight: null,
  fallbackSelector: null,
  mode: 'prospect',
  length: 'both',
  domain: 'dashboard',
  videoFallback: null
};

const welcomeOnboarding = {
  id: 'welcome',
  title: 'Bienvenido a VEKTOR',
  description: 'Plataforma unificada de simulación de ataques, detección y respuesta. Detection-as-Code (Sigma + Atomic Red Team), correlación per-tenant y MITRE ATT&CK como columna vertebral.',
  icon: Sparkles,
  position: 'center',
  highlight: null,
  fallbackSelector: null,
  mode: 'onboarding',
  length: 'both',
  domain: 'dashboard',
  videoFallback: null
};

// ============ DASHBOARD (clave en quick) ============
const dashboardProspect = {
  id: 'dashboard',
  title: 'Tu seguridad en una sola pantalla',
  description: 'Puntuación de seguridad, vulnerabilidades críticas, equipos activos y rutas de ataque detectadas. Toda la información del CISO en una vista.',
  icon: Shield,
  position: 'bottom-right',
  highlight: '[data-testid="dashboard-page"]',
  fallbackSelector: 'main',
  mode: 'prospect',
  length: 'both',
  domain: 'dashboard',
  videoFallback: null
};

const dashboardOnboarding = {
  id: 'dashboard',
  title: 'Dashboard ejecutivo',
  description: 'KPIs en tiempo real: Security Posture, agentes activos, vulnerabilidades por severidad, cobertura MITRE y tendencia 4 semanas. Datos calculados sobre los eventos reales del tenant.',
  icon: Shield,
  position: 'bottom-right',
  highlight: '[data-testid="dashboard-page"]',
  fallbackSelector: 'main',
  mode: 'onboarding',
  length: 'both',
  domain: 'dashboard',
  videoFallback: null
};

// ============ POSTURE SCORE (solo full) ============
const postureScoreProspect = {
  id: 'posture-score',
  title: 'Nota de seguridad 0-100',
  description: 'Una puntuación clara que entienden dirección y consejo. Si bajas de 70, hay un problema. Las 3 acciones prioritarias salen automáticamente.',
  icon: Gauge,
  position: 'bottom',
  highlight: '[data-testid="posture-score-widget"]',
  fallbackSelector: '[data-testid="dashboard-posture"]',
  mode: 'prospect',
  length: 'full',
  domain: 'dashboard',
  videoFallback: null
};

const postureScoreOnboarding = {
  id: 'posture-score',
  title: 'Security Posture Score',
  description: 'Score 0-100 calculado sobre vulnerabilidades activas, agentes desconectados, reglas de detección y compliance drift. Top 3 acciones priorizadas por impacto/esfuerzo.',
  icon: Gauge,
  position: 'bottom',
  highlight: '[data-testid="posture-score-widget"]',
  fallbackSelector: '[data-testid="dashboard-posture"]',
  mode: 'onboarding',
  length: 'full',
  domain: 'dashboard',
  videoFallback: null
};

// ============ THREAT MAP (solo full) ============
const threatMapProspect = {
  id: 'threat-map',
  title: 'Mapa visual de amenazas',
  description: 'Ve de un vistazo qué partes de tu red están bajo presión. Útil para reuniones con dirección sin tener que explicar tecnicismos.',
  icon: MapTrifold,
  position: 'top',
  highlight: '[data-testid="threat-map-widget"]',
  fallbackSelector: '[data-testid="dashboard-threatmap"]',
  mode: 'prospect',
  length: 'full',
  domain: 'dashboard',
  videoFallback: null
};

const threatMapOnboarding = {
  id: 'threat-map',
  title: 'Threat Map embebido',
  description: 'Topología animada hub→nodes con pulsos. Beam glow en aristas críticas. Detalle por host con vulnerabilidades activas y alertas correlacionadas. Página completa en /threat-map.',
  icon: MapTrifold,
  position: 'top',
  highlight: '[data-testid="threat-map-widget"]',
  fallbackSelector: '[data-testid="dashboard-threatmap"]',
  mode: 'onboarding',
  length: 'full',
  domain: 'dashboard',
  videoFallback: null
};

// ============ RISK EVOLUTION (solo full) ============
const riskEvolutionProspect = {
  id: 'risk-evolution',
  title: 'Evolución del riesgo',
  description: 'Cómo cambia tu nivel de exposición semana a semana. Si la línea sube, hay que actuar. Si baja, las acciones están funcionando.',
  icon: ChartLineUp,
  position: 'top',
  highlight: '[data-testid="risk-evolution-widget"]',
  fallbackSelector: '[data-testid="dashboard-risk-evolution"]',
  mode: 'prospect',
  length: 'full',
  domain: 'dashboard',
  videoFallback: null
};

const riskEvolutionOnboarding = {
  id: 'risk-evolution',
  title: 'Risk Evolution (4 semanas)',
  description: 'KPIs reales sobre ventana móvil de 28 días: nuevas vulns, alertas críticas, agentes caídos, drift de compliance. Sirve para evidenciar tendencia en steering committees.',
  icon: ChartLineUp,
  position: 'top',
  highlight: '[data-testid="risk-evolution-widget"]',
  fallbackSelector: '[data-testid="dashboard-risk-evolution"]',
  mode: 'onboarding',
  length: 'full',
  domain: 'dashboard',
  videoFallback: null
};

// ============ COMPLETE (clave en quick) ============
const completeProspect = {
  id: 'complete',
  title: 'Listo para explorar',
  description: 'Esto es solo el principio. Cuando quieras profundizar en cualquier sección, usa la búsqueda rápida (Ctrl+K) o pide una demo guiada.',
  icon: CheckCircle,
  position: 'center',
  highlight: null,
  fallbackSelector: null,
  mode: 'prospect',
  length: 'both',
  domain: 'dashboard',
  videoFallback: null
};

const completeOnboarding = {
  id: 'complete',
  title: 'Tour completado',
  description: 'Ya conoces el mapa. Configura agentes, importa reglas Sigma, ejecuta tu primer Atomic test y revisa la cobertura MITRE. Búsqueda rápida con Ctrl+K.',
  icon: CheckCircle,
  position: 'center',
  highlight: null,
  fallbackSelector: null,
  mode: 'onboarding',
  length: 'both',
  domain: 'dashboard',
  videoFallback: null
};

export const domainSteps = {
  prospect: {
    quick: [welcomeProspect, dashboardProspect, completeProspect],
    full: [welcomeProspect, dashboardProspect, postureScoreProspect, threatMapProspect, riskEvolutionProspect, completeProspect]
  },
  onboarding: {
    quick: [welcomeOnboarding, dashboardOnboarding, completeOnboarding],
    full: [welcomeOnboarding, dashboardOnboarding, postureScoreOnboarding, threatMapOnboarding, riskEvolutionOnboarding, completeOnboarding]
  }
};
